import ms from 'ms';
const TTL_KEY = 'ttl';
const PASSPHRASE_KEY = 'passphrase';
const ADDED_AT_KEY = 'addedAt';
const INACTIVE_AT_KEY = 'inactiveAt';
export class PassphraseCache {
    #alarms;
    #storage;
    #autoLockTimerAtom;
    #logger;
    #maxTtl;
    constructor({ storage, alarms, autoLockTimerAtom, logger, config }) {
        this.#storage = storage;
        this.#alarms = alarms;
        this.#autoLockTimerAtom = autoLockTimerAtom;
        this.#logger = logger;
        this.#maxTtl = (config.maxTtl || 60) * ms('1m');
    }
    #getTtl = async () => {
        return this.#autoLockTimerAtom.get();
    };
    #scheduleClear = async () => {
        const ttl = await this.#getTtl();
        this.#logger.log('rescheduling cache clear', ttl / ms('1m'));
        await this.#alarms.clear('clear-passphrase');
        await this.#alarms.create('clear-passphrase', {
            delayInMinutes: ttl / ms('1m'),
        });
    };
    async set(passphrase) {
        this.#logger.log('caching passphrase');
        const ttl = await this.#getTtl();
        await this.#storage.batchSet({
            [PASSPHRASE_KEY]: passphrase,
            [ADDED_AT_KEY]: Date.now(),
            [TTL_KEY]: ttl,
        });
    }
    async get() {
        const [passphrase, addedAt, inactiveAt, ttl] = (await this.#storage.batchGet([
            PASSPHRASE_KEY,
            ADDED_AT_KEY,
            INACTIVE_AT_KEY,
            TTL_KEY,
        ]));
        if (passphrase) {
            if (inactiveAt) {
                if (inactiveAt + ttl > Date.now()) {
                    this.#logger.log('fetched cached passphrase, in activity period');
                    return passphrase;
                }
            }
            else if (addedAt + this.#maxTtl > Date.now()) {
                this.#logger.log('fetched cached passphrase');
                return passphrase;
            }
            this.#logger.log('fetched expired passphrase, clearing and preventing unlock');
            void this.clear();
        }
        this.#logger.log('passphrase not in cache');
    }
    async changeTtl(ttl) {
        const newTtl = Math.min(this.#maxTtl, ttl);
        await this.#autoLockTimerAtom.set(newTtl);
        const passphrase = await this.#storage.get(PASSPHRASE_KEY);
        if (passphrase) {
            await this.#storage.set(TTL_KEY, newTtl);
            void this.#scheduleClear();
        }
    }
    async scheduleClear() {
        const passphrase = await this.#storage.get(PASSPHRASE_KEY);
        if (passphrase) {
            await this.#storage.set(INACTIVE_AT_KEY, Date.now());
            void this.#scheduleClear();
        }
    }
    async clear() {
        this.#logger.log('clearing cache');
        await this.#storage.clear();
    }
}
const createPassphraseCache = (args) => new PassphraseCache(args);
const passphraseCacheDefinition = {
    type: 'module',
    id: 'passphraseCache',
    factory: createPassphraseCache,
    dependencies: ['storage', 'alarms', 'autoLockTimerAtom', 'logger', 'config'],
};
export default passphraseCacheDefinition;
